/*
 * User avatar: FinTechUI's ProfilePicture for people, a round group icon for groups.
 *
 * Every avatar in the app goes through this wrapper, so call sites just render an avatar
 * and this is the single place that decides how a subject is shown.
 */
import React from 'react';
import { ProfilePicture } from '@amzn/fintech-ui';
import Icon from '@amzn/uno-awsui-components-react-themed/icon';
import './userAvatarStyles.scss';

export interface UserAvatarProps {
  /** Amazon login of the user (or the group name) to show. */
  userAlias: string;
  /** ProfilePicture size token. */
  size?: 'small' | 'medium' | 'large';
  /** Extra class, applied to whichever element is rendered. */
  className?: string;
  /** Forwarded as `data-testid` so tests locate the avatar either way. */
  testId?: string;
  /** The subject is a group (LDAP/POSIX/ANT), which has no badge photo: show a group icon. */
  isGroup?: boolean;
}

/**
 * Renders a user's avatar.
 * @param {UserAvatarProps} props Alias, size and optional styling/test hooks.
 * @returns {JSX.Element} FinTechUI's ProfilePicture, or a group icon for groups.
 */
const UserAvatar = ({
  userAlias,
  size = 'medium',
  className,
  testId,
  isGroup = false,
}: UserAvatarProps): JSX.Element => {
  if (isGroup) {
    const classes = ['user-avatar-group', `user-avatar-group--${size}`];
    if (className) classes.push(className);
    return (
      <span className={classes.join(' ')} role="img" aria-label={`${userAlias} (group)`} data-testid={testId}>
        <Icon name="group" />
      </span>
    );
  }

  return (
    <ProfilePicture userAlias={userAlias} size={size} className={className} testId={testId} />
  );
};

export default UserAvatar;
